/**
 * MED-1106 — The single shared rule for how a coach session reads everywhere a
 * coach sees it (dashboard Upcoming list, Needs Attention cards, cohort-detail
 * session rows). It is the front-end half of the "one shared rule" (AC-L3); the
 * topic itself is resolved server-side by the shared SQL resolver
 * (`RESOLVED_TOPIC_EXPR` = COALESCE(topic_override, session_number_config.topic,
 * topics.name)) and exposed as `topic_name`, so every surface — and the
 * session-page header (MED-1085) — reads identically.
 *
 * The rule (consistent `Session N: X` shape, never a bare "Session N"):
 *   - Topic present (AC-L1)              → `Session {N}: {topic}`
 *   - No topic, group coaching (AC-L2)   → `Session {N}: {group}` (composed here,
 *                                          NOT the raw `{group} - Session N` title)
 *   - Workshop / no group or number      → the stored `title` (workshop title)
 *
 * Coach audience (AC-L4): `topic_name` is the internal resolved topic — the
 * MED-832 participant display-name override is deliberately not applied here.
 */
export interface CoachSessionLabelInput {
  /** Session ordinal; null for sessions without a number (e.g. some workshops). */
  session_number: number | null;
  /** Server-resolved internal topic (full coalesce). Null when no topic is set. */
  topic_name: string | null;
  /** Group name, used to compose the no-topic label. Omit for non-group sessions. */
  group_name?: string | null;
  /** Stored session title — the fallback for workshops / unnumbered sessions. */
  title: string;
}

export function composeCoachSessionLabel({
  session_number,
  topic_name,
  group_name,
  title,
}: CoachSessionLabelInput): string {
  const topic = topic_name?.trim();
  if (session_number != null && topic) {
    return `Session ${session_number}: ${topic}`;
  }

  const group = group_name?.trim();
  if (session_number != null && group) {
    return `Session ${session_number}: ${group}`;
  }

  // Workshops (no group/topic) and any unnumbered edge case keep the stored
  // title — the server never fabricates a bare "Session N", and neither do we.
  return title;
}

/**
 * MED-1360: a session's group name for display, or null when it has none. A
 * 1:1 has no group, and the API sends null for it. The text "null" is treated
 * as no name too, so a label never reads "(null)".
 */
export function sessionGroupName(group_name: string | null | undefined): string | null {
  const name = group_name?.trim();
  return name && name !== 'null' ? name : null;
}
